import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { NotificationSoundService } from '../notification-sound/notification-sound.service';
import { OperatorSocketService } from '../socket/operator-socket.service';

/**
 * Tracks the number of new (PENDING, unclaimed) handoffs that arrived while
 * the operator was not on the /available page.
 *
 * Increments when a PENDING handoff upsert arrives off /available.
 * Decrements (removes from set) when the same handoff is subsequently taken.
 * Clears to zero when the operator navigates to any /available route.
 */
@Injectable({ providedIn: 'root' })
export class HandoffBadgeService {
  private readonly socket = inject(OperatorSocketService);
  private readonly router = inject(Router);
  private readonly sound = inject(NotificationSoundService);

  private readonly newHandoffIds = new Set<string>();
  private readonly _count = signal(0);

  /** Read-only signal. Bind to this in the template. */
  readonly count = this._count.asReadonly();

  constructor() {
    // New / updated handoff arrived via socket
    this.socket.handoffUpserted$.subscribe(({ handoff }) => {
      const onAvailablePage = this.router.url.startsWith('/available');

      if (handoff.status === 'PENDING' && !handoff.taken_by_admin_id) {
        if (!onAvailablePage && !this.newHandoffIds.has(handoff.id)) {
          this.newHandoffIds.add(handoff.id);
          this._count.set(this.newHandoffIds.size);
          this.sound.playNewHandoff();
        }
      } else {
        // Handoff was taken or closed — remove from the unseen set
        if (this.newHandoffIds.delete(handoff.id)) {
          this._count.set(this.newHandoffIds.size);
        }
      }
    });

    // Clear badge when operator navigates to /available (or a sub-route)
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        if (e.urlAfterRedirects.startsWith('/available')) {
          this.clear();
        }
      });
  }

  clear(): void {
    this.newHandoffIds.clear();
    this._count.set(0);
  }
}